import * as React from "react";
import { Alert, Snackbar } from "@mui/material";
import {
  Document,
  Person,
  Account,
  AccountNextStep,
  ServiceProvider,
  ServiceProviderType,
  CreateDocFunc,
} from "@/types";
import { ExtendedAccount } from "@/types/ExtendedAccount";
import { cleanedErrors } from "@/utils/Functions";
import { useUpdater } from "@/utils/callbacks";
import {
  errorsOfProperty,
  propertyAccountFieldsFromRecord,
  PropertyFields,
  PropertyFieldRecord,
  propertyRecordFromAccount,
  PropertyStatus,
  Property,
} from "../PropertyFields";
import { Record as ServiceProviderRecord } from "../ServiceProviderFields";
import {
  accountNumberAccountFieldsFromRecord,
  AccountNumberField,
  accountNumberErrorsOf,
  AccountNumberRecord,
  accountNumberRecordFromAccount,
} from "./AccountNumberField";
import {
  accountFieldsFromRecord as energyMeterInfoFieldsFromRecord,
  EnergyMeterInfoFields,
  errorsOf as errorsOfEnergyMeterInfoFields,
  EnergyMeter as EnergyMeterInfoFieldsRecord,
  recordFromAccount as energyMeterInfoFieldsRecordFromAccount,
} from "./EnergyMeterInfoFields";
import {
  waterAccountFieldsFromRecord,
  errorsOfWaterAccountFields,
  WaterAccountFieldsRecord,
  waterAccountFieldsRecordFromAccount,
  WaterAccountFields,
} from "./WaterAccountFields";
import { NestedErrors } from "@/utils/Types";
import {
  errorsOfResponsible,
  Responsible,
  ResponsibleForm,
  responsibleFromAccount,
} from "../ResponsibleFields/ResponsibleFields";

type UtilitiesAccount = {
  readonly property?: PropertyFieldRecord;
  readonly energyMeterInfoFields: EnergyMeterInfoFieldsRecord;
  readonly waterAccountFields: AccountNumberRecord & WaterAccountFieldsRecord;
  readonly responsible?: Responsible;
};

type UtilitiesAccountErrors = NestedErrors<UtilitiesAccount> | undefined;

export function errorsOf(
  record: UtilitiesAccount,
  version: "energy" | "water",
  propertyStatus: any
): UtilitiesAccountErrors {
  return cleanedErrors({
    property: errorsOfProperty(record?.property),
    responsible:
      propertyStatus === PropertyStatus.ExistingOccupier
        ? undefined
        : errorsOfResponsible(record?.responsible, AccountNextStep.Cancel),
    energyMeterInfoFields:
      version !== "energy"
        ? undefined
        : errorsOfEnergyMeterInfoFields(record?.energyMeterInfoFields),
    waterAccountFields:
      version !== "water"
        ? undefined
        : cleanedErrors({
            ...errorsOfWaterAccountFields(record?.waterAccountFields),
            ...accountNumberErrorsOf(record?.waterAccountFields),
          }),
  });
}

const recordFromAccount = (account: Account | undefined): UtilitiesAccount => {
  if (!account) {
    return {
      energyMeterInfoFields: energyMeterInfoFieldsRecordFromAccount(undefined),
      waterAccountFields: {
        ...waterAccountFieldsRecordFromAccount(),
        ...accountNumberRecordFromAccount(),
      },
    };
  }

  return {
    property: propertyRecordFromAccount(account),
    responsible: responsibleFromAccount(account),
    energyMeterInfoFields: energyMeterInfoFieldsRecordFromAccount(account),
    waterAccountFields: {
      ...waterAccountFieldsRecordFromAccount(account),
      ...accountNumberRecordFromAccount(account),
    },
  };
};

export type UtilitiesAccountFieldsProps = {
  readonly properties: ReadonlyArray<Property>;
  readonly persons: ReadonlyArray<Person>;
  readonly serviceProvider: ServiceProviderRecord;
  readonly account?: Account;
  readonly setBusy: (b: boolean) => void;
  readonly uploadedFileInfo: (id: string) => Promise<Document>;
  readonly createDocument: CreateDocFunc;
  readonly onAccountAdded: (
    a: ExtendedAccount,
    p?: {
      newServiceProvider?: ServiceProvider;
      newPerson?: Person;
      newProperty?: Property;
    }
  ) => void;
  readonly version: "energy" | "water";
  readonly updateTemplate?: (p: any) => void;
  readonly saveAccount: (r: { sector: ServiceProviderType; record: any }) => Promise<any>;
  readonly saveRecord?: (r: any) => void;
  readonly savedRecord?: any;
};

export const UtilitiesAccountFields: React.FC<UtilitiesAccountFieldsProps> = ({
  properties,
  persons,
  serviceProvider,
  account,
  setBusy,
  createDocument,
  uploadedFileInfo,
  onAccountAdded,
  version,
  updateTemplate,
  savedRecord,
  saveAccount,
  saveRecord,
}) => {
  const [record, update] = React.useState(savedRecord || recordFromAccount(account));
  const [errors, setErrors] = React.useState({} as UtilitiesAccountErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);

  const updateEnergyMeterInfoFields = useUpdater(update, "energyMeterInfoFields");
  const updateEnergyMeterErrors = useUpdater(setErrors, "energyMeterInfoFields");
  const updateWaterAccountFields = useUpdater(update, "waterAccountFields");
  const updateProperty = useUpdater(update, "property");

  const updateAccountHolder = useUpdater(update, "responsible");

  const propertyStatus = React.useMemo(() => {
    const propertyId = record.property?.propertyId;
    if (propertyId) {
      const property = properties.find(({ id }) => propertyId === id);
      return property?.status || record.property?.propertyStatus; // the property might not have a status (address only)
    }

    return record.property?.propertyStatus;
  }, [record.property?.propertyId, record.property?.propertyStatus, properties]);

  React.useEffect(() => {
    if (
      (propertyStatus === PropertyStatus.Vacant || propertyStatus === PropertyStatus.NewOccupier) &&
      !record.responsible
    ) {
      updateAccountHolder({});
    }

    if (propertyStatus === PropertyStatus.ExistingOccupier && record.responsible) {
      updateAccountHolder(undefined);
    }
  }, [record.responsible, propertyStatus, updateAccountHolder]);

  React.useEffect(
    processUtilitiesAccount(
      updateTemplate,
      serviceProvider,
      record,
      version,
      propertyStatus,
      setErrors,
      saveRecord,
      setBusy,
      account,
      saveAccount,
      setRemoteErrors,
      onAccountAdded
    ),
    [
      updateTemplate,
      serviceProvider,
      account,
      onAccountAdded,
      record,
      setBusy,
      version,
      propertyStatus,
      saveAccount,
      saveRecord,
    ]
  );

  return (
    <>
      {version === "water" && (
        <AccountNumberField
          record={record.waterAccountFields}
          update={updateWaterAccountFields}
          errors={errors?.waterAccountFields}
        />
      )}

      <PropertyFields
        serviceProvider={serviceProvider}
        serviceProviderId={serviceProvider.serviceProviderId}
        persons={persons}
        properties={properties}
        record={record.property || {}}
        errors={errors?.property}
        update={updateProperty}
      />

      {record.responsible && (
        <ResponsibleForm
          serviceProvider={serviceProvider}
          persons={persons}
          nextStep={AccountNextStep.Cancel}
          record={record.responsible}
          errors={errors?.responsible}
          update={updateAccountHolder}
        />
      )}

      {version === "energy" && (
        <EnergyMeterInfoFields
          record={record.energyMeterInfoFields}
          errors={errors?.energyMeterInfoFields}
          update={updateEnergyMeterInfoFields}
          updateError={updateEnergyMeterErrors}
          uploadedFileInfo={uploadedFileInfo}
          createDocument={createDocument}
        />
      )}
      {version === "water" && (
        <WaterAccountFields
          record={record.waterAccountFields}
          errors={errors?.waterAccountFields}
          update={updateWaterAccountFields}
          propertyStatus={propertyStatus}
          serviceProviderId={serviceProvider.serviceProviderId}
        />
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};
function processUtilitiesAccount(
  updateTemplate: ((p: any) => void) | undefined,
  serviceProvider: ServiceProviderRecord,
  record: any,
  version: "energy" | "water",
  propertyStatus: any,
  setErrors: React.Dispatch<React.SetStateAction<NestedErrors<UtilitiesAccount> | undefined>>,
  saveRecord: ((r: any) => void) | undefined,
  setBusy: (b: boolean) => void,
  account: Account | undefined,
  saveAccount: (r: { sector: ServiceProviderType; record: any }) => Promise<any>,
  setRemoteErrors: React.Dispatch<React.SetStateAction<string | undefined>>,
  onAccountAdded: (
    a: ExtendedAccount,
    p?: {
      newServiceProvider?: ServiceProvider;
      newPerson?: Person;
      newProperty?: Property;
    }
  ) => void
): React.EffectCallback {
  return () => {
    if (!updateTemplate) return;

    updateTemplate({
      onNext: () => {
        if (!serviceProvider?.serviceProviderType) return undefined;
        const errors = errorsOf(record, version, propertyStatus);

        if (errors) {
          setErrors(errors);
          if (saveRecord) saveRecord({ source: record });
          return;
        }

        setBusy(true);

        const data = {
          id: account?.id,
          serviceProvider: {
            id: serviceProvider.serviceProviderId,
            companyName: serviceProvider.customProviderName,
            serviceProviderType: serviceProvider.serviceProviderType,
          },

          ...propertyAccountFieldsFromRecord(record.property),
          responsible: record.responsible && {
            id: record.responsible?.responsibleId || undefined,
            details: record.responsible?.responsible || undefined,
            bankAccount: record.responsible?.responsibleBankAccount || undefined,
          },
          ...(version !== "energy"
            ? {}
            : energyMeterInfoFieldsFromRecord(record.energyMeterInfoFields)),
          ...(version !== "water"
            ? {}
            : {
                ...waterAccountFieldsFromRecord(record.waterAccountFields),
                ...accountNumberAccountFieldsFromRecord(record.waterAccountFields),
              }),
          // documentIds,
        };

        if (saveRecord) {
          saveRecord({ source: record, target: data });
          return { response: {} };
        }

        // @ts-ignore
        return saveAccount({ sector: version, record: data }).then(
          (
            response: {
              data: {
                account: ExtendedAccount;
                newServiceProvider?: ServiceProvider;
                newPerson?: Person;
                newProperty?: Property;
              };
            } & { error: Error }
          ) => {
            setBusy(false);
            if (response.error) {
              setRemoteErrors(response.error.message);
              return;
            }
            if (response.data) {
              onAccountAdded(response.data.account, {
                newServiceProvider: response.data.newServiceProvider,
                newPerson: response.data.newPerson,
                newProperty: response.data.newProperty,
              });
              return;
            }
          },
          (err: Error) => {
            console.warn({ err });
            setBusy(false);
            setRemoteErrors("Operation failed. Please try again or contact customer support.");
          }
        );
      },
    });
  };
}
